import { Skeleton, VStack } from '@chakra-ui/react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { Navigate, useParams } from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import {
  OUTSOURCE_MEMBER_CONTEXT_NAMES,
  usePostOutsourceMemberSearch,
} from '../../../../hooks/queries/outsourceMember';
import { usePostWorkerAssignOutsourceMemberSearch } from '../../../../hooks/queries/workerAssign';
import useToastHook from '../../../../hooks/ToastHook';
import { OutsourceMemberSearchResult } from '../../../../models/gen/outsourceMemberSearchResult';
import { useSizeContext } from '../../../SizeProvider';
import Wireframe from '../../../wireframe/Wireframe';
import { WorkerAssignmentFormState } from './assign-form/FormArea';
import WorkerAssignmentContent from './Content';

/**
 * 外注メンバアサイン画面パラメータ
 */
export type WorkerAssignmentParams = {
  /**
   * 日毎現場ID
   */
  dailyMatterId: string;
};

/**
 * 外注メンバアサイン画面
 *
 * - URL `/outsourcer/worker-assignment/:dailyMatterId`
 * - パラメータ:
 *   - dailyMatterId: 日毎現場ID
 * - クエリ: なし
 *
 * @returns
 */
const WorkerAssignment: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const params = useParams<WorkerAssignmentParams>();
  const sizes = useSizeContext();
  const queryClient = useQueryClient();
  const member = queryClient.getQueryData<OutsourceMemberSearchResult>(
    OUTSOURCE_MEMBER_CONTEXT_NAMES.POST_OUTSOURCE_MEMBER_SEARCH
  );
  const setMember = React.useCallback(
    (result: OutsourceMemberSearchResult) =>
      queryClient.setQueryData(
        OUTSOURCE_MEMBER_CONTEXT_NAMES.POST_OUTSOURCE_MEMBER_SEARCH,
        result
      ),
    [queryClient]
  );

  const form = useForm<WorkerAssignmentFormState>();
  const { data, error, isError, isLoading, refetch, isFetched } =
    usePostWorkerAssignOutsourceMemberSearch({
      dailyMatterId: parseInt(params.dailyMatterId!),
      outsourcerCode: state.userInfo?.outsourcer_code,
    });
  const postOutsourceMemberSearchResult = usePostOutsourceMemberSearch();

  React.useEffect(() => {
    refetch();

    return () => form.reset({});
  }, []);
  const { toast } = useToastHook();
  React.useEffect(() => {
    postOutsourceMemberSearchResult.mutate(
      {
        date: data?.result?.date,
        outsourcerCode: state.userInfo!.outsourcer_code,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setMember(result);
          }
        },
        onError: (error) =>
          toast(error instanceof AppError ? error : 'メンバ検索に失敗しました'),
      }
    );

    return () => form.reset({});
  }, [data?.result?.date]);
  React.useEffect(() => {
    if (!isLoading) {
      form.reset({
        ...data?.result,
        leader: data?.result?.members
          ?.find((member) => member.isLeader)
          ?.assignWorkerId?.toString(),
      });
    }
  }, [isLoading, data?.result]);
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError
          ? error
          : 'メンバアサイン情報の取得に失敗しました'
      );
    }
  }, [isError]);

  React.useEffect(() => {
    if (postOutsourceMemberSearchResult.isError) {
      toast(
        postOutsourceMemberSearchResult.error instanceof AppError
          ? postOutsourceMemberSearchResult.error
          : 'メンバ情報の取得に失敗しました'
      );
    }
  }, [postOutsourceMemberSearchResult.isError]);

  return (
    <Wireframe
      isLoaded={!isLoading && isFetched}
      title={pageNames.WORKER_ASSIGNMENT}
    >
      <FormProvider {...form}>
        <VStack
          alignItems="start"
          fontSize={sizes.fontSize.rawMessage}
          width={sizes.width.styledMessageList}
        >
          <WorkerAssignmentContent
            members={member?.results}
            result={data?.result}
          />
        </VStack>
      </FormProvider>
    </Wireframe>
  );
};

export default WorkerAssignment;
